import { useMemo, useCallback } from 'react';
import useDataStore from '../store/useDataStore';
import useAppStore from '../store/useAppStore';
import {
  addRecipe, updateRecipe, deleteRecipe, toggleFavorite, markAsCooked, submitReview,
  getMealPlan, updateMealPlan, getActiveGroceryList,
  addToGroceryList, toggleGroceryItem, removeGroceryItem,
  clearCheckedItems, clearGroceryList, addGroceryItemsFromRecipes,
  toggleUnitSystem, triggerSync,
} from '../services/data';

// AppProvider is a passthrough now — Dexie data lives in useDataStore (synced via
// useDexieSync), UI state in useAppStore. Kept as a no-op so any straggling
// imports keep compiling during the migration.
export function AppProvider({ children }) {
  return children;
}

// Composed hook — pulls Dexie data + UI state, computes derivatives, returns the same
// shape the legacy Context did so the 22 consumer files don't need touching.
export function useApp() {
  // Dexie-backed data
  const recipes = useDataStore((s) => s.recipes);
  const categories = useDataStore((s) => s.categories);
  const groceryLists = useDataStore((s) => s.groceryLists);
  const groceryItems = useDataStore((s) => s.groceryItems);
  const mealPlans = useDataStore((s) => s.mealPlans);
  const initialized = useDataStore((s) => s.initialized);
  const syncing = useDataStore((s) => s.syncing);

  // UI state
  const theme = useAppStore((s) => s.theme);
  const setTheme = useAppStore((s) => s.setTheme);
  const sidebarOpen = useAppStore((s) => s.sidebarOpen);
  const setSidebarOpen = useAppStore((s) => s.setSidebarOpen);
  const searchQuery = useAppStore((s) => s.searchQuery);
  const setSearchQuery = useAppStore((s) => s.setSearchQuery);
  const activeCategory = useAppStore((s) => s.activeCategory);
  const setActiveCategory = useAppStore((s) => s.setActiveCategory);
  const activeCuisine = useAppStore((s) => s.activeCuisine);
  const setActiveCuisine = useAppStore((s) => s.setActiveCuisine);
  const activeAgeGroup = useAppStore((s) => s.activeAgeGroup);
  const setActiveAgeGroup = useAppStore((s) => s.setActiveAgeGroup);
  const activeDifficulty = useAppStore((s) => s.activeDifficulty);
  const setActiveDifficulty = useAppStore((s) => s.setActiveDifficulty);
  const activeDietary = useAppStore((s) => s.activeDietary);
  const setActiveDietary = useAppStore((s) => s.setActiveDietary);
  const unitSystem = useAppStore((s) => s.unitSystem);

  const filteredRecipes = useMemo(() => {
    let result = recipes;
    if (activeCategory && activeCategory !== 'All') {
      if (activeCategory === 'Favorites') {
        result = result.filter((r) => r.favorite);
      } else {
        result = result.filter((r) => r.categories?.includes(activeCategory));
      }
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter((r) =>
        r.title?.toLowerCase().includes(q) ||
        r.tags?.some((t) => t.toLowerCase().includes(q)) ||
        r.ingredients?.some((i) => i.item?.toLowerCase().includes(q)) ||
        r.description?.toLowerCase().includes(q)
      );
    }
    if (activeCuisine !== 'All') result = result.filter((r) => r.cuisine === activeCuisine);
    if (activeAgeGroup !== 'All') result = result.filter((r) => r.ageGroups?.includes(activeAgeGroup));
    if (activeDifficulty !== 'All') result = result.filter((r) => r.difficulty === activeDifficulty);
    if (activeDietary.length > 0) {
      result = result.filter((r) => activeDietary.every((tag) => r.tags?.includes(tag)));
    }
    return result;
  }, [recipes, activeCategory, searchQuery, activeCuisine, activeAgeGroup, activeDifficulty, activeDietary]);

  const savedRecipes = useMemo(() => recipes.filter((r) => r.favorite), [recipes]);
  const savedCount = savedRecipes.length;

  const trendingRecipes = useMemo(
    () => [...recipes].sort((a, b) => (b.communityRating || 0) - (a.communityRating || 0)).slice(0, 8),
    [recipes]
  );

  const newRecipes = useMemo(
    () => [...recipes].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 8),
    [recipes]
  );

  const getRecipesByCategory = useCallback((category, limit = 6) => {
    return recipes.filter((r) => r.categories?.includes(category)).slice(0, limit);
  }, [recipes]);

  const getSimilarRecipes = useCallback((recipe, limit = 6) => {
    if (!recipe) return [];
    return recipes
      .filter((r) => r.id !== recipe.id && (r.cuisine === recipe.cuisine || r.categories?.some((c) => recipe.categories?.includes(c))))
      .sort((a, b) => (b.communityRating || 0) - (a.communityRating || 0))
      .slice(0, limit);
  }, [recipes]);

  const availableCuisines = useMemo(() => {
    const cuisines = [...new Set(recipes.map((r) => r.cuisine).filter(Boolean))];
    return cuisines.sort();
  }, [recipes]);

  return {
    initialized,
    syncing,
    recipes,
    filteredRecipes,
    categories,
    searchQuery, setSearchQuery,
    activeCategory, setActiveCategory,
    activeCuisine, setActiveCuisine,
    activeAgeGroup, setActiveAgeGroup,
    activeDietary, setActiveDietary,
    activeDifficulty, setActiveDifficulty,
    unitSystem, toggleUnitSystem,
    availableCuisines,
    submitReview,
    theme, setTheme,
    sidebarOpen, setSidebarOpen,
    addRecipe, updateRecipe, deleteRecipe,
    toggleFavorite, markAsCooked,
    mealPlans, getMealPlan, updateMealPlan,
    groceryLists, groceryItems,
    getActiveGroceryList,
    addToGroceryList,
    toggleGroceryItem,
    removeGroceryItem,
    clearCheckedItems,
    clearGroceryList,
    addGroceryItemsFromRecipes,
    triggerSync,
    savedRecipes,
    savedCount,
    trendingRecipes,
    newRecipes,
    getRecipesByCategory,
    getSimilarRecipes,
  };
}
